<template>
  <div class="mt-4">
    <d-field label="Group Access">
      <d-autocomplete
        v-model="searchGroupValue"
        open-on-focus
        :loading="isFetchingGroups"
        :data="availableGroups"
        placeholder="Select a group..."
        field="name"
        position="top"
        @typing="attemptGetGroups"
        @input="val => addToGroupArray(val)"
      >
        <template v-slot="{option}">
          <p>{{ option.name }}</p>
          <p class="text-sm text-gray-600">
            Group/Community
          </p>
        </template>
        <template slot="empty">
          No results found
        </template>
      </d-autocomplete>
    </d-field>
    <div class="my-3 flex flex-wrap">
      <d-chip
        v-for="group in selectedGroups"
        :key="group.id"
        size="sm"
        label
        closable
        class="mr-2 mb-3"
        @close="selectedGroups = selectedGroups.filter(m => group.id !== m.id)"
      >
        {{ group.name }}
      </d-chip>
    </div>

    <d-field label="User Access">
      <d-autocomplete
        v-model="searchUserValue"
        open-on-focus
        position="top"
        :loading="isFetchingUsers"
        :data="availableUsers"
        item-key="userId"
        placeholder="Select a user..."
        field="fullName"
        @typing="attemptGetUsers"
        @input="val => addToUsersArray(val)"
      >
        <template v-slot="{option}">
          <p>{{ option.fullName }}</p>
          <p class="text-sm text-gray-600">
            {{ option.email }}
          </p>
        </template>
        <template slot="empty">
          No results found
        </template>
      </d-autocomplete>
    </d-field>
    <div class="my-3 flex flex-wrap">
      <d-chip
        v-for="user in selectedUsers"
        :key="user.userId"
        size="sm"
        label
        closable
        class="mr-2 mb-3"
        @close="selectedUsers = selectedUsers.filter(m => user.userId !== m.userId)"
      >
        {{ user.name }}
      </d-chip>
    </div>
  </div>
</template>
<script>
  import axios from 'axios';
  import debounce from 'lodash/debounce';
  export default {
    parent: true,
    props: {
      entityId: {
        type: Number,
        default: () => 0,
      },
      entityType: {
        type: String,
        default: () => '',
      },
    },

    data() {
      return {
        currentEntityPermissions: [],

        isFetchingUsers: false,
        isFetchingGroups: false,

        searchUserValue: '',
        searchGroupValue: '',

        selectedUsers: [],
        selectedGroups: [],

        availableUsers: [],
        availableGroups: [],
      };
    },

    mounted() {
      this.getUsersDataAsync();
      this.getGroupsDataAsync();
      this.getPermissionsForEntity();
    },

    methods: {
      async getPermissionsForEntity() {
        if (this.entityId != 0) {
          const { data } = await axios.get(
            `/api/permissions/entity/${this.entityId}/${this.entityType}`,
          );
          this.currentEntityPermissions = data;

          for (let i = 0; i < this.currentEntityPermissions.length; i++) {
            if (this.currentEntityPermissions[i].userId) {
              this.selectedUsers.push({
                userId: this.currentEntityPermissions[i].userId,
                groupId: null,
                name: this.currentEntityPermissions[i].permissionGivenTo,
                entityId: this.currentEntityPermissions[i].entityId,
                entityType: this.entityType,
              });
            } else {
              this.selectedGroups.push({
                userId: null,
                groupId: this.currentEntityPermissions[i].groupId,
                name: this.currentEntityPermissions[i].permissionGivenTo,
                entityId: this.currentEntityPermissions[i].entityId,
                entityType: this.entityType,
              });
            }
          }
        }
      },

      getUsersDataAsync: debounce(async function (name = '') {
        try {
          const { data } = await axios.get(`/api/user-lookup?filter=${name}`);
          this.availableUsers = data;
        } catch (error) {
          this.availableUsers = [];
          throw error;
        } finally {
          this.isFetchingUsers = false;
        }
      }, 500),

      attemptGetUsers(name) {
        this.isFetchingUsers = true;
        this.getUsersDataAsync(name);
      },

      getGroupsDataAsync: debounce(async function (name = '') {
        try {
          const { data } = await axios.get(`/api/group-lookup?filter=${name}`);
          this.availableGroups = data;
        } catch (error) {
          this.availableGroups = [];
          throw error;
        } finally {
          this.isFetchingGroups = false;
        }
      }, 500),

      attemptGetGroups(name) {
        this.isFetchingGroups = true;
        this.getGroupsDataAsync(name);
      },

      addToUsersArray(userId) {
        if (userId) {
          const selectedUser = this.availableUsers.find((x) => x.userId == userId);

          if (selectedUser) {
            if (
              !this.selectedUsers.find((m) => m.userId == selectedUser.userId)
            ) {
              this.selectedUsers.push({
                userId: selectedUser.userId,
                groupId: null,
                entityId: this.entityId,
                name: selectedUser.fullName,
                entityType: this.entityType,
              });
            }
          }
          this.searchUserValue = '';
          this.$nextTick(() => {
            this.searchUserValue = null;
          });
        }
      },

     addToGroupArray (groupId) {
        if (groupId) {
          const selectedGroup = this.availableGroups.find((x) => x.id == groupId);

          if (selectedGroup) {
            if (
              !this.selectedGroups.find((m) => m.id == selectedGroup.id)
            ) {
              this.selectedGroups.push({
                userId: null,
                groupId,
                entityId: this.entityId,
                name: selectedGroup.name,
                entityType: this.entityType,
              });
            }
          }

          this.searchGroupValue = '';
          this.$nextTick(() => {
            this.searchGroupValue = null;
          });
        }
      },

      async saveData(entityId) {
        const submitData = [];

        for (let i = 0; i < this.selectedUsers.length; i++) {
          this.selectedUsers[i].entityId = entityId;
          submitData.push(this.selectedUsers[i]);
        }

        for (let i = 0; i < this.selectedGroups.length; i++) {
          this.selectedGroups[i].entityId = entityId;
          submitData.push(this.selectedGroups[i]);
        }

        try {
          await axios.put(`/api/permissions/bulk?entityId=${entityId}&entityType=${this.entityType}`, submitData);
        } catch (error) {
          this.$toast.open({ message: error.response.data, color: 'red-500', duration: 4000 });
        }
      },
    },
  };
</script>